import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Description as DocumentIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import API_CONFIG from '../config/api';
import { getAuthenticatedUploadUrl, apiFetch } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = API_CONFIG.BACKEND_URL;

// Componente de Card de Regulamento
const RegulamentoCard = ({ regulamento, onEdit, onDelete }) => {
  const handleOpenPdf = () => {
    if (regulamento.arquivo_path) {
      const pdfUrl = getAuthenticatedUploadUrl(`regulamentos/${regulamento.arquivo_path}`);
      window.open(pdfUrl, '_blank');
    }
  };

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.3)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
      onClick={handleOpenPdf}
    >
      {/* Imagem do Documento */}
      <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
        <CardMedia
          component="div"
          sx={{
            height: '100%',
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          {/* Ícone do Documento */}
          <DocumentIcon sx={{ fontSize: 80, color: 'white', opacity: 0.8 }} />
          
          {/* Ícone PDF sobreposto */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              background: 'rgba(255,255,255,0.9)',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}
          >
            <PdfIcon sx={{ color: '#d32f2f', fontSize: 24 }} />
          </Box>

          {/* Badge de Tipo */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              background: 'rgba(255,255,255,0.9)',
              borderRadius: 1,
              px: 1,
              py: 0.5
            }}
          >
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {regulamento.tipo || 'Regulamento'}
            </Typography>
          </Box>
        </CardMedia>
      </Box>

      {/* Conteúdo do Card */}
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1, color: 'primary.main' }}>
          {regulamento.nome}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
          {regulamento.descricao || 'Documento regulamentar'}
        </Typography>

        {/* Informações do Documento */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Chip 
            label={`${regulamento.tamanho_arquivo || 'PDF'}`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary">
            {regulamento.data_cadastro ? new Date(regulamento.data_cadastro).toLocaleDateString('pt-BR') : 'Recente'}
          </Typography>
        </Box>

        {/* Botões de Ação */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(regulamento);
            }}
            sx={{ 
              background: 'rgba(25,118,210,0.1)',
              '&:hover': { background: 'rgba(25,118,210,0.2)' }
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(regulamento);
            }}
            sx={{ 
              background: 'rgba(211,47,47,0.1)',
              '&:hover': { background: 'rgba(211,47,47,0.2)' }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default function RegulamentosPage() {
  const { unidadeSelecionada } = useAuth();
  const [regulamentos, setRegulamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Função para buscar regulamentos
  const fetchRegulamentos = async () => {
    try {
      setLoading(true);
      // Adicionar filtro de unidade na URL
      const url = unidadeSelecionada 
        ? `${BACKEND_URL}/api/regulamentos?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `${BACKEND_URL}/api/regulamentos`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Erro ao buscar regulamentos');
      }
      
      const data = await response.json();
      setRegulamentos(data);
    } catch (error) {
      console.error('Erro ao carregar regulamentos:', error);
      setError('Erro ao carregar regulamentos');
    } finally {
      setLoading(false);
    }
  };

  // Função para editar regulamento
  const handleEdit = (regulamento) => {
    // Disparar evento para abrir modal de edição
    window.dispatchEvent(new CustomEvent('openRegulamentoModal', { 
      detail: { regulamento, mode: 'edit' } 
    }));
  };

  // Função para deletar regulamento
  const handleDelete = async (regulamento) => {
    if (window.confirm(`Tem certeza que deseja excluir o regulamento "${regulamento.nome}"?`)) {
      try {
        const response = await apiFetch(`/api/regulamentos/${regulamento.id}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          await fetchRegulamentos();
          alert('Regulamento excluído com sucesso!');
        } else {
          throw new Error('Erro ao excluir regulamento');
        }
      } catch (error) {
        console.error('Erro ao excluir regulamento:', error);
        alert('Erro ao excluir regulamento');
      }
    }
  };

  // Função para adicionar novo regulamento
  const handleAdd = () => {
    window.dispatchEvent(new CustomEvent('openRegulamentoModal', { 
      detail: { mode: 'add' } 
    }));
  };

  useEffect(() => {
    fetchRegulamentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on unidadeSelecionada change only
  }, [unidadeSelecionada]);

  // Listener para atualizar lista quando modal é fechado
  useEffect(() => {
    const handleRegulamentoSaved = () => {
      fetchRegulamentos();
    };

    window.addEventListener('regulamentoSaved', handleRegulamentoSaved);
    
    return () => {
      window.removeEventListener('regulamentoSaved', handleRegulamentoSaved);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- listener setup once on mount
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Carregando regulamentos...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={fetchRegulamentos}>
          Tentar Novamente
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e3f2fd 50%, #f8fafc 100%)',
      p: { xs: 1, sm: 2 },
      m: 0,
      overflow: 'auto'
    }}>
      {/* Cabeçalho */}
      <Box 
        sx={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          p: { xs: 2, sm: 3 },
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          border: '1px solid rgba(255,255,255,0.2)',
          mb: 3,
          position: 'relative'
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
          <Box sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            boxShadow: '0 4px 12px rgba(25,118,210,0.3)'
          }}>
            <DocumentIcon sx={{ color: 'white', fontSize: '24px' }} />
          </Box>
          <Typography 
            variant="h5" 
            fontWeight={700} 
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Regulamentos
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Documentos regulamentares e normativos da empresa
        </Typography>

        {/* Botão Adicionar */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)'
            }
          }}
        >
          Adicionar Regulamento
        </Button>
      </Box>

      {/* Grid de Regulamentos */}
      {regulamentos.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <DocumentIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            Nenhum regulamento cadastrado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Clique em "Adicionar Regulamento" para começar
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              boxShadow: '0 4px 12px rgba(25,118,210,0.3)'
            }}
          >
            Adicionar Primeiro Regulamento
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {regulamentos.map((regulamento) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={regulamento.id}>
              <RegulamentoCard
                regulamento={regulamento}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
